import { loadWithChunkRetry } from '@muse-web/router'

import { requestToaster, waitForToasterMount } from './toasterRequest'

type TSonnerToast = typeof import('sonner').toast

type TAsync<TFn> = TFn extends (...args: infer TArgs) => infer TResult
    ? (...args: TArgs) => Promise<Awaited<TResult>>
    : TFn

type TToastId = Parameters<TSonnerToast['dismiss']>[0]

type TAsyncToast = TAsync<TSonnerToast> & {
    [TName in Exclude<keyof TSonnerToast, 'dismiss'>]: TAsync<TSonnerToast[TName]>
} & {
    dismiss: TAsync<(id?: TToastId | Promise<TToastId>) => ReturnType<TSonnerToast['dismiss']>>
}

const TOASTER_MOUNT_TIMEOUT_MS = 2000

let loading: Promise<TSonnerToast> | null = null

const loadToast = (): Promise<TSonnerToast> => {
    loading ??= loadWithChunkRetry(() => import(/* webpackChunkName: 'sonner' */ 'sonner')).then(
        module => module.toast,
        (error: unknown) => {
            loading = null
            throw error
        },
    )

    return loading
}

const resolveArgs = (name: string | symbol, args: unknown[]) =>
    name === 'dismiss' ? Promise.all(args) : Promise.resolve(args)

export const toast = new Proxy((() => undefined) as unknown as TAsyncToast, {
    get:
        (_target, name) =>
        async (...args: unknown[]) => {
            if (name !== 'dismiss') {
                requestToaster()
                await waitForToasterMount(TOASTER_MOUNT_TIMEOUT_MS)
            }

            const sonner = await loadToast()
            const member = Reflect.get(sonner, name)

            return typeof member === 'function'
                ? member.apply(sonner, await resolveArgs(name, args))
                : member
        },
    apply: async (_target, _thisArg, args: unknown[]) => {
        requestToaster()
        await waitForToasterMount(TOASTER_MOUNT_TIMEOUT_MS)

        return (await loadToast())(...(args as Parameters<TSonnerToast>))
    },
})
